<template>
    <div class="parts-player-footer">
        <div class="parts-player-footer__left">
            <slot name="extra-controls-before" />
            <button
                v-show="!playing"
                type="button"
                class="parts-player-footer__button parts-player-footer__button--toggle"
                :aria-label="$t('partsPlayer.play')"
                :title="$t('partsPlayer.play')"
                @click.prevent="play"
            >
                <IconPlayOutline stroke="#fff" />
            </button>
            <button
                v-show="playing"
                type="button"
                class="parts-player-footer__button parts-player-footer__button--toggle"
                :aria-label="$t('partsPlayer.pauseAction')"
                :title="$t('partsPlayer.pauseAction')"
                @click.prevent="pause"
            >
                <IconPauseOutline stroke="#fff" />
            </button>
            <slot name="extra-controls-after" />
        </div>

        <div class="parts-player-footer__main">
            <div class="parts-player-footer__now-playing">
                <slot name="now-playing" />
            </div>
            <div
                ref="bar"
                class="player-progress"
                @mouseenter="onBarMouseEnter"
                @mousemove="onBarMouseMove"
                @mouseleave="onBarMouseLeave"
                @click="seek"
            >
                <div ref="progress" class="player-progress__bar" />
                <div ref="loaded" class="player-progress__loaded" />
                <!-- Decorative drag handle: seeking is mouse-only here, as in PlayerSimple,
                     so keep it out of the tab order instead of a dead, unlabeled tab stop. -->
                <button
                    ref="handle"
                    type="button"
                    class="player-progress__handle"
                    tabindex="-1"
                    aria-hidden="true"
                />
            </div>
        </div>

        <div class="parts-player-footer__right">
            <span class="parts-player-footer__duration">
                {{ seekTime || timeString }} / {{ durationString }}
            </span>
            <VDropdown v-if="!hideRate" placement="top">
                <button type="button" class="parts-player-footer__rate">{{ rate }}x</button>
                <template #popper>
                    <div class="player__rate-list">
                        <button
                            v-for="r in playbackRates"
                            :key="r"
                            v-close-popper
                            type="button"
                            class="player__rate-button button button--badge"
                            @click="setRate(r)"
                        >
                            {{ r }}x
                        </button>
                    </div>
                </template>
            </VDropdown>
            <VDropdown v-if="!hideVolume" placement="top">
                <button
                    type="button"
                    class="parts-player-footer__volume"
                    :aria-label="$t('misc.volume')"
                    :title="$t('misc.volume')"
                >
                    <IconVolumeOutline :muted="volume <= 0" />
                </button>
                <template #popper>
                    <div class="player__volume">
                        <input
                            v-model.number="volume"
                            class="player__volume-range"
                            max="1"
                            min="0"
                            step="0.05"
                            type="range"
                        />
                    </div>
                </template>
            </VDropdown>
        </div>

        <audio ref="audio" class="player__audio" :src="src" controls />
    </div>
</template>

<script>
import playerMixin from '@speechki-frontend/shared/ui/Player/playerMixin';
import {
    IconPlayOutline,
    IconPauseOutline,
    IconVolumeOutline,
} from '@speechki-frontend/shared/ui/icons';

// Professional Prooflistening (embedded outsourced reviewer) bottom player
// bar — a dedicated template matching the Spines Design System mock's exact
// single-row grouping (left transport cluster / middle now-playing+progress
// column / right time+speed+volume cluster), which the shared PlayerSimple
// component's own linear layout can't reproduce via CSS alone without
// restructuring it for every other consumer. Reuses playerMixin so audio
// state/timing/seek/rate/volume/localStorage logic is not duplicated —
// PlayerSimple.vue uses the exact same mixin.
export default {
    name: 'PartsPlayerFooter',
    components: {
        IconPlayOutline,
        IconPauseOutline,
        IconVolumeOutline,
    },
    mixins: [playerMixin],
};
</script>

<style lang="scss" src="./PartsPlayerFooter.scss"></style>
